import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { verifyToken } from '../services/AuthApi';
import { getToken, removeToken, clearSessionData } from '../utils/tokenUtils';

/**
 * Custom hook to verify token validity
 * - Verifies token on mount
 * - Detects manual localStorage changes
 * - Redirects to signup page on invalid/expired token
 */
export const useTokenVerification = () => {
  const navigate = useNavigate();
  const lastTokenRef = useRef(null);
  const verificationTimeoutRef = useRef(null);

  const handleInvalidToken = (message = "Token has expired") => {
    console.warn('[TokenVerification]', message);
    
    // Clear token and session data
    removeToken();
    clearSessionData();
    
    // Redirect to signin page
    navigate('/signin', { replace: true });
  };

  const checkTokenValidity = async () => {
    const token = getToken();
    
    // If no token exists, do nothing (user may be on public page)
    if (!token) {
      return;
    }

    try {
      const response = await verifyToken();
      
      if (!response.token_valid) {
        handleInvalidToken(response.message || "Token is invalid");
      }
    } catch (error) {
      console.error('[TokenVerification] Error verifying token:', error);
      handleInvalidToken(error.message || "Token verification failed");
    }
  };

  // Check for localStorage tampering
  const detectTokenTampering = () => {
    const currentToken = getToken();
    
    // If token was removed or changed externally
    if (lastTokenRef.current && currentToken !== lastTokenRef.current) {
      console.warn('[TokenVerification] Token was manually modified in localStorage');
      handleInvalidToken("Security check failed: Token was modified");
    }
    
    lastTokenRef.current = currentToken;
  };

  useEffect(() => {
    const token = getToken();
    if (!token) {
      return; // No token, nothing to verify
    }

    // Store initial token value
    lastTokenRef.current = token;

    // Verify token on mount
    checkTokenValidity();

    // Set up periodic verification (every 2 minutes)
    verificationTimeoutRef.current = setInterval(() => {
      checkTokenValidity();
    }, 2 * 60 * 1000); // 2 minutes

    // Listen for localStorage changes (manual tampering detection)
    const handleStorageChange = (e) => {
      // Check if the token key was modified
      if (e.key && (e.key === 'fc_token_pwa' || e.key === 'fc_token_web' || e.key === 'fc_token')) {
        detectTokenTampering();
      }
    };

    // Set up interval to check for localStorage tampering
    // (storage event doesn't fire in same window, so we poll)
    const tamperCheckInterval = setInterval(() => {
      detectTokenTampering();
    }, 2000); // Check every 2 seconds

    // Listen for storage events from other tabs
    window.addEventListener('storage', handleStorageChange);

    // Cleanup
    return () => {
      if (verificationTimeoutRef.current) {
        clearInterval(verificationTimeoutRef.current);
      }
      clearInterval(tamperCheckInterval);
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []); // Empty dependency array - only run on mount

  return {
    checkTokenValidity,
  };
};

export default useTokenVerification;
